'use client';

import { useEffect, useState } from 'react';
import { tryCatch, err, ok, type Result, type AppError } from '@shared/utils';
import type { TelehealthMeetingComponentProps } from '../../src/interfaces/TelehealthMeetingComponentProps';

/**
 * Decodes JWT payload without verification (client-side only; token is pre-validated).
 * Extracts appId from 'sub' claim for JaaSMeeting.
 */
function getAppIdFromToken(token: string): Result<string, AppError> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return err({ errorCode: 'INVALID_TOKEN', errorMessage: 'Token does not have 3 parts' });
  }
  const decodeResult = tryCatch(() => {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  });
  if (decodeResult.isErr) {
    return err({ errorCode: 'TOKEN_DECODE_ERROR', errorMessage: 'Failed to decode JWT payload' });
  }
  const payload = decodeResult.value;
  if (typeof payload.sub !== 'string') {
    return err({ errorCode: 'MISSING_SUB_CLAIM', errorMessage: 'Token payload missing sub claim' });
  }
  return ok(payload.sub);
}

/**
 * @group Components
 *
 * @summary
 * <blockquote>Browser-only wrapper for Jitsi JaaSMeeting. Dynamically imports
 * @jitsi/react-sdk to avoid SSR; maps TelehealthMeetingComponentProps to JaaSMeeting.</blockquote>
 *
 * @example
 * ```tsx
 * <JitsiMeetingWrapper
 *   displayName="Dr. Smith"
 *   roomName="consultation-123"
 *   serverUrl="https://8x8.vc"
 *   token="eyJ..."
 * />
 * ```
 */
export function JitsiMeetingWrapper(
  props: TelehealthMeetingComponentProps
): React.ReactElement {
  const [MeetingComponent, setMeetingComponent] = useState<
    React.ComponentType<{
      appId: string;
      roomName: string;
      jwt?: string;
      onReadyToClose?: () => void;
      userInfo?: { displayName: string; email: string };
      getIFrameRef?: (iframe: HTMLDivElement) => void;
    }> | null
  >(null);

  useEffect(() => {
    void import('@jitsi/react-sdk').then((mod) => {
      setMeetingComponent(() => mod.JaaSMeeting);
    });
  }, []);

  if (!MeetingComponent || typeof window === 'undefined') {
    return <div data-testid="jitsi-loading">Loading...</div>;
  }

  const appIdResult = getAppIdFromToken(props.token);
  if (appIdResult.isErr) {
    return <div data-testid="jitsi-error">Invalid token</div>;
  }
  const appId = appIdResult.value;

  const handleIFrameRef = (iframe: HTMLDivElement) => {
    if (!iframe) return;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    if (iframe.parentElement) {
      iframe.parentElement.style.width = '100%';
      iframe.parentElement.style.height = '100%';
    }
  };

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <MeetingComponent
        appId={appId}
        roomName={props.roomName}
        jwt={props.token}
        onReadyToClose={props.onReadyToClose}
        userInfo={{ displayName: props.displayName, email: '' }}
        getIFrameRef={handleIFrameRef}
      />
    </div>
  );
}
